import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { Factory, TrendingUp, Truck, Award, Users, CheckCircle2, MapPin } from "lucide-react";

export default function Infrastructure() {
  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Deep Industry Experience",
      description: "25+ years Industrial experience"
    },
    {
      icon: <Factory className="w-8 h-8" />,
      title: "Massive Production Scale",
      description: "15 production houses"
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Operational Speed",
      description: "Logistic network"
    },
    {
      icon: <CheckCircle2 className="w-8 h-8" />,
      title: "Comprehensive Product Range",
      description: "Wide variety of minerals"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Guaranteed Quality",
      description: "Certified and tested"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Visionary Team",
      description: "Expert leadership"
    }
  ];

  const industries = [
    {
      title: "Tiles Industries",
      description: "The modern tiles industry is a dynamic sector that supplies a vast selection of surface coverings, including specialized ceramic, porcelain, and vitrified varieties, alongside timeless natural stone options. These products are essential across residential, commercial, and industrial construction, valued globally for their exceptional durability, low maintenance, and aesthetic versatility. They are instrumental in finishing floors, walls, and decorative areas, offering a rich choice of designs, textures, and finishes to suit any architectural style.",
      icon: "🏗️"
    },
    {
      title: "Dinnerware",
      description: "Ceramic dinnerware is a durable staple, forged from a refined mixture of clay, feldspar, and kaolin. After being carefully shaped using traditional or modern molding techniques, the piece is hardened in a kiln through a multi-stage thermal cycle. This process sets the form, allowing a glaze to be applied, which is then vitrified in a final firing. The result is a non-porous, resilient surface that elegantly combines natural materials with functional design for long-lasting use.",
      icon: "🍽️"
    },
    {
      title: "Sanitary Ware",
      description: "The Sanitary Ware Industry doesn't just produce fixtures-it designs the hygienic architecture of modern life. Using the ultimate blend of earth's durability, primarily vitreous china (a fired mix of clay, quartz, and feldspar), this sector delivers toilets, basins, and urinals that are both essential and elegant. The core appeal lies in the non-porous, glassy-smooth surface that ensures maximum cleanliness and longevity. From sleek, water-saving designs to antibacterial glazes, this industry transforms humble necessities into durable, design-forward statements that elevate comfort and safety in every residential and commercial setting.",
      icon: "🚿"
    },
    {
      title: "Refractory Industry",
      description: "The Refractory Industry is the unsung backbone of heavy manufacturing, dedicated to creating specialized, non-metallic materials essential for containing and controlling industrial heat. These products-made from resilient ceramics like magnesite, alumina, and fireclays-form the critical linings of furnaces, kilns, and reactors. Engineered to endure relentless extreme temperatures, chemical attack, and mechanical wear, refractories ensure the efficiency, longevity, and safety of high-temperature processes across vital sectors, including steel, cement, glass, and energy production worldwide.",
      icon: "🔥"
    },
    {
      title: "Additives & Paint Sealants",
      description: "The Additives & Paint Sealants sector is the secret laboratory where surfaces gain their superpower. It is the engine of innovation that takes basic coatings and engineers them for peak performance, flawless application, and enduring brilliance. These essential chemical compounds and mineral fine-tuners are designed to master every variable: the thickeners that prevent drips, the dispersants that maximize color saturation, and the sealants that lock out the elements. This industry ensures that whether a finish is battling road salt on an automotive chassis, corrosion on an industrial pipeline, or fading UV rays on a skyscraper, the result is a high-definition, resilient surface where protection and breathtaking aesthetics converge.",
      icon: "🎨"
    },
    {
      title: "Insulation Industry",
      description: "The Insulation Industry is the master of sustainable silence and efficiency, specializing in materials that form an impenetrable barrier against unwanted energy transfer. It manufactures the critical liners-from fire-resistant mineral wool and fiberglass to high-performance ceramic insulators-that actively battle heat loss, noise pollution, and electrical surge. By integrating robust minerals like kaolin, talc, and quartz, the industry delivers products with unmatched thermal stability, electrical resistance, and durability. This is the essential science that makes modern life functional, guaranteeing peak energy efficiency and safety across construction, automotive, and high-tech manufacturing worldwide.",
      icon: "🛡️"
    }
  ];

  return (
    <Layout>
      {/* Hero Section - Matching About Page Style */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Infrastructure
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Strategic Mining Operations & Advanced Processing Facilities
          </p>
        </div>
      </section>

      {/* Main Infrastructure Description */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl shadow-xl p-8 md:p-12 border border-blue-100">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-6 text-center">
                Integrated Mining & Processing Excellence
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our robust infrastructure is supported by dedicated mining operations at strategic locations. We own and operate <strong className="text-[#1e3a8a]">Potash Mines at Dantda Rajsamand</strong> and <strong className="text-[#1e3a8a]">Soda Mines in Udaipur</strong>, both renowned for their superior quality mineral deposits.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                These prime mining locations enable us to source high-grade raw materials directly from the origin, ensuring consistent supply and uncompromised product quality. Combined with our advanced processing units, in-house laboratories, and extensive distribution network, this integrated setup allows us to maintain excellence from extraction to delivery.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mining Locations */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Strategic Mining Locations
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Direct access to premium mineral deposits
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-[#2563eb] hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <MapPin className="w-6 h-6 text-[#1e3a8a]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2">Potash Mines</h3>
                  <p className="text-gray-600 font-medium">Dantda, Rajsamand</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Our Potash mining operations in Dantda, Rajsamand are strategically located in one of the richest mineral belts. These mines provide superior quality potash feldspar, essential for various industrial applications.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-[#2563eb] hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <MapPin className="w-6 h-6 text-[#1e3a8a]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2">Soda Mines</h3>
                  <p className="text-gray-600 font-medium">Udaipur</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Located in Udaipur, our Soda mines are renowned for producing high-grade soda feldspar. The strategic location ensures efficient extraction and processing of premium quality minerals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Us Best */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              What Makes Us Best
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our competitive advantages in the mineral industry
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-md p-6 border border-blue-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="bg-[#1e3a8a] text-white p-3 rounded-lg inline-block mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Powering excellence across diverse industrial sectors
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto space-y-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border-l-4 border-[#2563eb] hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-5xl">{industry.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-bold text-[#1e3a8a] mb-4">
                      {industry.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {industry.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Experience Our World-Class Infrastructure
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Partner with us for reliable, high-quality mineral supply backed by robust infrastructure
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-[#1e3a8a] px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg hover:shadow-xl"
            >
              Get in Touch
            </a>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
